/**
 * What changed on the Points page: balances that moved, credit amounts that
 * changed or appeared, and tiers that changed, one change per thing however
 * many times it moved since it was last dismissed, plus every balance reading
 * held for review, which Approve or Reject settles rather than a dismissal. Derived server-side from
 * rows Wallet already stores; dismissals are saved there too, so every device
 * agrees (server/wallet_core/points_changes.py).
 */
import { authedFetch } from "./api";
import { monthDay } from "./creditsPage";
import { orThrow } from "./http";

export type ChangeKind = "up" | "down" | "credit" | "new_credit" | "status" | "held";

/** The part of a change one capture run made. */
export interface RunPart {
  run_id: string;
  kind: ChangeKind;
  from: string | null;
  to: string;
  /** Null on a held reading with no counted balance before it. */
  delta?: string | null;
  used_up?: boolean;
}

export interface PointsChange {
  /** The thing: one balance, one credit or one tier. */
  key: string;
  /** Every move folded into this change; dismissing sends all of them. */
  keys: string[];
  moves: number;
  account_id: number;
  kind: ChangeKind;
  program: string;
  /** Set only when the program has more than one account. */
  account: string | null;
  /** The credit's name, for credit changes. */
  credit: string | null;
  from: string | null;
  to: string;
  delta?: string | null;
  used_up?: boolean;
  /** The held reading's event, for Approve and Reject; held changes only. */
  event_id?: number;
  /** Pacific day, YYYY-MM-DD. */
  on: string;
  /** The Credits page tracks this credit; the Points page hides its row. */
  on_credits_page: boolean;
  /** The newest move's capture run and that run's own part, or null. */
  run: RunPart | null;
}

export async function fetchChanges(): Promise<PointsChange[]> {
  const res = await authedFetch("/api/points/changes");
  const body = (await orThrow(res, "Could not load what changed")) as {
    changes: PointsChange[];
  };
  return body.changes;
}

async function post(path: string, keys: string[], failure: string): Promise<void> {
  const res = await authedFetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ keys }),
  });
  await orThrow(res, failure);
}

export function dismissChanges(keys: string[]): Promise<void> {
  return post("/api/points/changes/dismiss", keys, "Could not dismiss it");
}

export function undismissChanges(keys: string[]): Promise<void> {
  return post("/api/points/changes/undismiss", keys, "Could not undo that");
}

/** "Today", "Yesterday", or "Sep 16" for a change's Pacific day. */
export function changeDay(on: string, today: string): string {
  if (on === today) return "Today";
  const d = new Date(`${today}T12:00:00`);
  d.setDate(d.getDate() - 1);
  const pad = (n: number) => String(n).padStart(2, "0");
  if (on === `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`) {
    return "Yesterday";
  }
  return monthDay(on, today);
}
